import { DateTime } from "luxon";

/** Which tasks a calendar feed includes. Prisma's and GraphQL's `TaskCalendarScope` enums both fit. */
type CalendarFeedScope = "ALL" | "MINE" | "CUSTOM";

/**
 * Keys the feed sets itself, so a saved search must not carry them. `status`
 * describes a single occurrence (e.g. overdue), not a task.
 */
export const CALENDAR_FEED_RESERVED_KEYS = ["tenantId", "after", "before", "status"] as const;

type CalendarFeedSearchInput = {
  tenantId: string;
  userId: string;
  scope: CalendarFeedScope;
  /** Saved search without reserved keys. Only applied for CUSTOM. */
  search?: string | null;
  /** Set only for legacy facility-specific feeds. */
  facilityId?: string | null;
  now?: DateTime;
};

/** How far back and ahead of today a calendar feed's deadlines reach. */
export const CALENDAR_FEED_LOOKBACK = { months: 1 };
export const CALENDAR_FEED_LOOKAHEAD = { years: 1 };

/**
 * The omnisearch string for a calendar feed. The ICS feed and the subscription
 * preview both use it, so the preview shows what the calendar will get.
 */
export function buildCalendarFeedSearch({
  tenantId,
  userId,
  scope,
  search,
  facilityId,
  now = DateTime.now(),
}: CalendarFeedSearchInput): string {
  const parts = [
    `tenantId:${tenantId}`,
    `after:${now.minus(CALENDAR_FEED_LOOKBACK).toISODate()}`,
    `before:${now.plus(CALENDAR_FEED_LOOKAHEAD).toISODate()}`,
  ];
  if (facilityId) parts.push(`facilityId:${facilityId}`);
  if (scope === "MINE") parts.push(`involvesUserId:${userId}`);
  if (scope === "CUSTOM" && search?.trim()) parts.push(search.trim());
  return parts.join(" ");
}
